import React, { useState, useEffect, useMemo } from 'react';
import { CustomerOrder, OrderStatus, PaymentStatus } from '../../types';
import { useContent } from '../../context/ContentContext';
import { matchesOutlet } from '../../data/adminAccounts';
import {
  fetchAllOrders,
  fetchSingleOrder,
  updateOrderStatus,
  deleteOrder,
  subscribeToOrdersRealtime,
  ORDERS_SQL_SCHEMA,
} from '../../utils/supabaseOrders';
import {
  formatRupiah,
  formatOrderTime,
  getJakartaDateKey,
  shiftJakartaDateKey,
  formatJakartaDateKeyId,
} from '../../utils/formatters';
import { getApiUrl } from '../../utils/api';
import {
  ShoppingBag,
  Clock,
  Utensils,
  Search,
  CheckCircle2,
  Volume2,
  VolumeX,
  Code,
  Copy,
  Check,
  Store,
  Eye,
  XCircle,
  X,
  FileCheck,
  Printer,
  ExternalLink,
  ShieldCheck,
  Building2,
  Lock,
  Trash2,
  Coffee,
  Bell,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight,
  History,
  TrendingUp,
  Power,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { KitchenSlipModal } from './KitchenSlipModal';
import { OrderDetailModal } from './OrderDetailModal';
import {
  subscribeToOutletAvailabilityRealtime,
  updateOutletAvailability,
  updateOutletSchedule,
  normalizeOutletKey,
  resolveOutletOperatingStatus,
  DEFAULT_OUTLET_SCHEDULES,
} from '../../utils/supabaseOutletStatus';
import { OutletScheduleConfig, OutletScheduleMode } from '../../types';

// Compact item customization summary helper (Stitch Design)
const getItemCustomizationSummary = (it: any): string => {
  const parts: string[] = [];
  if (it.size && it.size.name) parts.push(it.size.name);
  if (it.topping && it.topping.name && it.topping.name !== 'No Topping') parts.push(it.topping.name);
  if (it.syrup && it.syrup.name && it.syrup.name !== 'No Syrup') parts.push(it.syrup.name);
  if (it.customOptions && Array.isArray(it.customOptions) && it.customOptions.length > 0) {
    it.customOptions.forEach((c: any) => {
      if (c && c.optionName) parts.push(c.optionName);
    });
  }
  if (it.note) {
    parts.push(`Note: ${it.note}`);
  }
  return parts.join(' • ');
};

// Compact relative timestamp helper in Asia/Jakarta (WIB) timezone (Stitch Design)
const getOrderTimestamp = (createdAt: string) => {
  try {
    const d = new Date(createdAt);
    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - d.getTime());
    const diffMins = Math.floor(diffMs / 60000);
    const timeStr = new Intl.DateTimeFormat('id-ID', {
      timeZone: 'Asia/Jakarta',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
      .format(d)
      .replace('.', ':');
    let relStr = '';
    if (diffMins < 1) {
      relStr = '(baru saja)';
    } else if (diffMins < 60) {
      relStr = `(${diffMins}m lalu)`;
    } else {
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) {
        relStr = `(${diffHours}j lalu)`;
      }
    }
    return { timeStr: `${timeStr} WIB`, relStr };
  } catch {
    return { timeStr: '', relStr: '' };
  }
};

interface OrderManagerProps {
  initialViewMode?: 'today' | 'history';
  onViewModeChange?: (mode: 'today' | 'history') => void;
}

export const OrderManager: React.FC<OrderManagerProps> = ({
  initialViewMode = 'today',
  onViewModeChange,
}) => {
  const { auth, showToast } = useContent();
  const isOutletAdmin = auth.role === 'outlet_admin';
  const assignedOutletId = auth.outletId || '';
  const assignedOutletName = auth.outletName || (isOutletAdmin ? 'Outlet Ditugaskan' : 'Semua Cabang');

  // View Mode: 'today' (Pesanan Hari Ini) | 'history' (Riwayat Pesanan)
  const [viewMode, setViewMode] = useState<'today' | 'history'>(initialViewMode);

  useEffect(() => {
    setViewMode(initialViewMode);
  }, [initialViewMode]);

  const handleSwitchViewMode = (mode: 'today' | 'history') => {
    setViewMode(mode);
    setSelectedStatusFilter('ALL');
    setSelectedPaymentFilter('ALL');
    setSearchQuery('');
    if (onViewModeChange) {
      onViewModeChange(mode);
    }
  };

  // Current Jakarta Date Key (YYYY-MM-DD in WIB) with periodic refresh across midnight
  const [todayDateKey, setTodayDateKey] = useState<string>(() => getJakartaDateKey(new Date()));

  useEffect(() => {
    const interval = setInterval(() => {
      const currentKey = getJakartaDateKey(new Date());
      if (currentKey && currentKey !== todayDateKey) {
        setTodayDateKey(currentKey);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [todayDateKey]);

  const yesterdayDateKey = useMemo(
    () => shiftJakartaDateKey(todayDateKey, -1),
    [todayDateKey]
  );

  // Selected Single Date for Riwayat Pesanan (Strictly < todayDateKey)
  const [selectedHistoryDate, setSelectedHistoryDate] = useState<string>(() =>
    shiftJakartaDateKey(getJakartaDateKey(new Date()), -1)
  );

  // Ensure selectedHistoryDate is never >= todayDateKey
  useEffect(() => {
    if (!selectedHistoryDate || selectedHistoryDate >= todayDateKey) {
      setSelectedHistoryDate(yesterdayDateKey);
    }
  }, [todayDateKey, yesterdayDateKey, selectedHistoryDate]);

  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedOutletFilter, setSelectedOutletFilter] = useState<string>(
    isOutletAdmin && assignedOutletId ? assignedOutletId : 'ALL'
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL');
  const [selectedPaymentFilter, setSelectedPaymentFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [isSqlModalOpen, setIsSqlModalOpen] = useState<boolean>(false);
  const [copiedSql, setCopiedSql] = useState<boolean>(false);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);

  // Lightbox & Modal states
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  // Rejection modal states
  const [rejectingOrder, setRejectingOrder] = useState<CustomerOrder | null>(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState<string>('');
  const [isSubmittingReject, setIsSubmittingReject] = useState<boolean>(false);

  // Deletion modal states
  const [orderToDelete, setOrderToDelete] = useState<CustomerOrder | null>(null);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState<boolean>(false);

  // Kitchen Slip / Checker modal states
  const [selectedOrderForSlip, setSelectedOrderForSlip] = useState<CustomerOrder | null>(null);
  const [isSlipModalOpen, setIsSlipModalOpen] = useState<boolean>(false);

  // Order Detail modal states
  const [selectedOrderDetail, setSelectedOrderDetail] = useState<CustomerOrder | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  // Outlet Order Availability & Schedule states
  const [outletsAvailability, setOutletsAvailability] = useState<Record<string, boolean>>({
    sudirman: true,
    kelakap_7: true,
    'letgo-mpp': true,
  });
  const [outletsSchedules, setOutletsSchedules] = useState<Record<string, OutletScheduleConfig>>(
    DEFAULT_OUTLET_SCHEDULES
  );
  const [updatingOutletKey, setUpdatingOutletKey] = useState<string | null>(null);
  const [editingScheduleOutlet, setEditingScheduleOutlet] = useState<string | null>(null);
  const [tempScheduleForm, setTempScheduleForm] = useState<OutletScheduleConfig>({
    mode: 'SCHEDULE',
    openTime: '08:00',
    closeTime: '23:00',
  });
  const [isSavingSchedule, setIsSavingSchedule] = useState<boolean>(false);

  // Realtime subscription for outlet availability and schedules
  useEffect(() => {
    const unsub = subscribeToOutletAvailabilityRealtime((statusMap, schedMap) => {
      setOutletsAvailability(statusMap);
      if (schedMap) {
        setOutletsSchedules(schedMap);
      }
    });
    return () => unsub();
  }, []);

  const handleToggleOutletStatus = async (outletId: string, currentStatus: boolean) => {
    const normKey = normalizeOutletKey(outletId);

    // RBAC validation check for Outlet Admin
    if (isOutletAdmin && !matchesOutlet(normKey, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat mengubah status outlet yang menjadi tanggung jawab Anda.', 'error');
      return;
    }

    const nextStatus = !currentStatus;
    setUpdatingOutletKey(normKey);

    try {
      const res = await updateOutletAvailability(normKey, nextStatus, auth.role, auth.outletId);
      if (res.success) {
        showToast(
          `Status ${normKey.toUpperCase()}: ${nextStatus ? '🟢 BUKA (Menerima Order)' : '🔴 TUTUP (Tidak Menerima Order)'}`,
          nextStatus ? 'success' : 'info'
        );
      } else {
        showToast(res.error || 'Gagal mengubah status penerimaan order.', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Terjadi kesalahan sistem saat mengubah status.', 'error');
    } finally {
      setUpdatingOutletKey(null);
    }
  };

  const handleOpenScheduleModal = (outletId: string) => {
    const normKey = normalizeOutletKey(outletId);
    const curr = outletsSchedules[normKey] || DEFAULT_OUTLET_SCHEDULES[normKey] || {
      mode: 'SCHEDULE',
      openTime: '08:00',
      closeTime: '23:00',
    };
    setTempScheduleForm({ ...curr });
    setEditingScheduleOutlet(normKey);
  };

  const handleSaveSchedule = async () => {
    if (!editingScheduleOutlet) return;
    setIsSavingSchedule(true);
    try {
      const res = await updateOutletSchedule(
        editingScheduleOutlet,
        tempScheduleForm,
        auth.role,
        auth.outletId
      );
      if (res.success) {
        showToast(`Jadwal & mode outlet ${editingScheduleOutlet.toUpperCase()} berhasil diperbarui!`, 'success');
        setEditingScheduleOutlet(null);
      } else {
        showToast(res.error || 'Gagal memperbarui jadwal outlet.', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Terjadi kesalahan sistem.', 'error');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  // New order notification state
  const [notificationState, setNotificationState] = useState<{
    showBanner: boolean;
    currentOrder: CustomerOrder | null;
    orderCount: number;
  }>({
    showBanner: false,
    currentOrder: null,
    orderCount: 0,
  });
  const notifiedOrderIdsRef = React.useRef<Set<string>>(new Set());

  const soundEnabledRef = React.useRef(soundEnabled);
  React.useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Play synthetic chime when new order arrives
  const playOrderChime = () => {
    if (!soundEnabledRef.current) return;
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const now = ctx.currentTime;

      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, now);
      gain1.gain.setValueAtTime(0, now);
      gain1.gain.linearRampToValueAtTime(0.4, now + 0.04);
      gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.4);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now + 0.14);
      gain2.gain.setValueAtTime(0, now + 0.14);
      gain2.gain.linearRampToValueAtTime(0.45, now + 0.18);
      gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.85);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.14);
      osc2.stop(now + 0.85);
    } catch (e) {
      console.warn('[Audio Notification Warning]:', e);
    }
  };

  // Determine active target outlet filter (Strict Outlet Isolation)
  const effectiveOutletScope = isOutletAdmin ? assignedOutletId : selectedOutletFilter;

  // Initial fetch and Realtime subscription
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsLoading(true);
      const targetId = effectiveOutletScope !== 'ALL' ? effectiveOutletScope : undefined;
      const data = await fetchAllOrders(targetId);
      if (mounted) {
        setOrders(data);
        setIsLoading(false);
      }
    };

    load();

    // Subscribe to realtime orders for this outlet scope
    const targetId = effectiveOutletScope !== 'ALL' ? effectiveOutletScope : undefined;
    const unsubscribe = subscribeToOrdersRealtime(
      (updatedList) => {
        if (mounted) {
          setOrders(updatedList);
        }
      },
      (newOrder) => {
        // Jangan pernah trigger chime atau banner untuk pesanan DANA yang belum lunas
        if (newOrder.paymentMethod === 'DANA' && newOrder.paymentStatus !== 'PAID') {
          return;
        }
        if (!notifiedOrderIdsRef.current.has(newOrder.id)) {
          notifiedOrderIdsRef.current.add(newOrder.id);
          playOrderChime();
          setNotificationState((prev) => ({
            showBanner: true,
            currentOrder: newOrder,
            orderCount: prev.orderCount + 1,
          }));
        }
      },
      targetId
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [effectiveOutletScope]);

  // Support deep link from push notification click and service worker messages
  useEffect(() => {
    if (orders.length === 0) return;

    const checkDeepLink = () => {
      const hashPart = window.location.hash || '';
      const queryIdx = hashPart.indexOf('?');
      if (queryIdx === -1) return;

      const queryString = hashPart.substring(queryIdx + 1);
      const searchParams = new URLSearchParams(queryString);
      const deepOrderId = searchParams.get('orderId');
      if (deepOrderId) {
        const foundOrder = orders.find((o) => o.id === deepOrderId || o.orderNumber === deepOrderId);
        if (foundOrder) {
          setSelectedOrderDetail(foundOrder);
          setIsDetailModalOpen(true);

          const cleanHash = hashPart.substring(0, queryIdx);
          window.history.replaceState(null, '', cleanHash);
        } else {
          fetchSingleOrder(deepOrderId).then((fetched) => {
            if (fetched) {
              setSelectedOrderDetail(fetched);
              setIsDetailModalOpen(true);
              const cleanHash = hashPart.substring(0, queryIdx);
              window.history.replaceState(null, '', cleanHash);
            }
          });
        }
      }
    };

    checkDeepLink();

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
        const oId = event.data.orderId;
        if (oId) {
          const foundOrder = orders.find((o) => o.id === oId || o.orderNumber === oId);
          if (foundOrder) {
            setSelectedOrderDetail(foundOrder);
            setIsDetailModalOpen(true);
          } else {
            fetchSingleOrder(oId).then((fetched) => {
              if (fetched) {
                setSelectedOrderDetail(fetched);
                setIsDetailModalOpen(true);
              }
            });
          }
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [orders]);

  // Handle status update - Awaits database confirmation & triggers Customer Push Notification
  const handleUpdateStatus = async (
    orderId: string,
    newStatus: OrderStatus,
    newPaymentStatus?: PaymentStatus,
    rejectionReason?: string,
    orderData?: CustomerOrder
  ) => {
    try {
      // 1. Direct synchronous update in Supabase & wait for database confirmation
      const success = await updateOrderStatus(orderId, newStatus, newPaymentStatus, rejectionReason, orderData);
      if (!success) {
        throw new Error('Gagal memperbarui status di cloud');
      }

      // Capture previous order before updating UI to check status delta & order number
      const prevOrder = orders.find((o) => o.id === orderId) || orderData;
      const prevStatus = prevOrder?.orderStatus;
      const targetOrderNumber = prevOrder?.orderNumber || orderData?.orderNumber;

      // 2. Only update UI state after database confirms success (strictly preserving Courier deliveryStatus & courierUsername)
      setOrders((prev) =>
        prev.map((o) => {
          if (o.id === orderId) {
            return {
              ...o,
              orderStatus: newStatus,
              order_status: newStatus,
              paymentStatus: newPaymentStatus || o.paymentStatus,
              rejectionReason: rejectionReason !== undefined ? rejectionReason : o.rejectionReason,
              deliveryStatus: o.deliveryStatus || o.delivery_status,
              delivery_status: o.delivery_status || o.deliveryStatus,
              courierUsername: o.courierUsername || o.courier_username,
              courier_username: o.courier_username || o.courierUsername,
              updatedAt: new Date().toISOString(),
            };
          }
          return o;
        })
      );

      // 3. Fire-and-forget Customer Status Push Notification (Non-blocking, decoupled from order transaction)
      const allowedPushStatuses: OrderStatus[] = ['ACCEPTED', 'PREPARING', 'READY', 'CANCELLED'];
      if (
        prevStatus !== newStatus &&
        allowedPushStatuses.includes(newStatus) &&
        targetOrderNumber
      ) {
        void (async () => {
          try {
            const adminToken = localStorage.getItem('leton_admin_token') || '';
            await fetch(getApiUrl('/api/onesignal/notify-customer'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                orderNumber: targetOrderNumber,
                status: newStatus,
                reason: rejectionReason,
              }),
            });
          } catch (pushErr) {
            console.warn('[Customer Push Notification Dispatch Notice]:', pushErr);
          }
        })();
      }
    } catch (err: any) {
      console.error('[Order Status Update Failed]:', err);
      const errMsg = err?.message || '';
      if (errMsg.includes('tidak ditemukan') || errMsg.includes('not found')) {
        setOrders((prev) => prev.filter((o) => o.id !== orderId));
        if (selectedOrderDetail?.id === orderId) {
          setIsDetailModalOpen(false);
          setSelectedOrderDetail(null);
        }
        showToast('Pesanan tidak ditemukan di Supabase (sudah dihapus sebelumnya). Tampilan diperbarui.', 'info');
      } else {
        showToast('Gagal memperbarui status pesanan: ' + (errMsg || 'Koneksi terputus') + '. Perubahan dibatalkan.', 'error');
      }
      throw err;
    }
  };

  // Action: NEW -> ACCEPTED [ TERIMA PESANAN ]
  const handleAcceptOrder = async (order: CustomerOrder) => {
    if (isOutletAdmin && !matchesOutlet(order.outletId, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat memproses pesanan di cabang Anda.', 'error');
      return;
    }
    setUpdatingOrderId(order.id);
    try {
      const nextPayment =
        (order.paymentMethod === 'QRIS' && order.paymentReceiptUrl) || order.paymentStatus === 'PAID'
          ? 'PAID'
          : order.paymentStatus;
      await handleUpdateStatus(order.id, 'ACCEPTED', nextPayment, undefined, order);
      showToast(`Pesanan #${order.orderNumber} diterima! Siap diproses.`, 'success');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Action: ACCEPTED -> PREPARING [ PROSES PESANAN / MULAI DISEDUH ]
  const handleStartBrewing = async (order: CustomerOrder) => {
    if (isOutletAdmin && !matchesOutlet(order.outletId, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat memproses pesanan di cabang Anda.', 'error');
      return;
    }
    setUpdatingOrderId(order.id);
    try {
      await handleUpdateStatus(order.id, 'PREPARING', undefined, undefined, order);
      showToast(`Pesanan #${order.orderNumber} sedang diproses oleh barista.`, 'success');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Action: READY -> COMPLETED [ SELESAIKAN PESANAN ]
  const handleCompleteOrder = async (order: CustomerOrder) => {
    if (isOutletAdmin && !matchesOutlet(order.outletId, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat memproses pesanan di cabang Anda.', 'error');
      return;
    }
    setUpdatingOrderId(order.id);
    try {
      const nextPayment = order.paymentStatus === 'PAID' ? 'PAID' : order.paymentStatus;
      await handleUpdateStatus(order.id, 'COMPLETED', nextPayment, undefined, order);
      showToast(`Pesanan #${order.orderNumber} selesai disajikan!`, 'success');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Action: PREPARING -> READY [ TANDAI SIAP DIAMBIL ]
  const handleMarkReady = async (order: CustomerOrder) => {
    if (isOutletAdmin && !matchesOutlet(order.outletId, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat memproses pesanan di cabang Anda.', 'error');
      return;
    }

    setUpdatingOrderId(order.id);
    try {
      if (order.orderStatus === 'READY') {
        await handleUpdateStatus(order.id, 'COMPLETED', undefined, undefined, order);
        showToast(`Pesanan #${order.orderNumber} telah ditandai SELESAI.`, 'success');
      } else {
        const nextPayment = order.paymentStatus;
        await handleUpdateStatus(order.id, 'READY', nextPayment, '', order);
        showToast(`Pesanan #${order.orderNumber} berhasil ditandai SIAP DIAMBIL ☕ (Poin otomatis diproses)`, 'success');
      }
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Action: [ TOLAK / BATALKAN ]
  const handleOpenRejectDialog = (order: CustomerOrder) => {
    if (isOutletAdmin && !matchesOutlet(order.outletId, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat memproses pesanan di cabang Anda.', 'error');
      return;
    }
    setRejectingOrder(order);
    setRejectionReasonInput('Stok bahan habis / toko sedang tutup / transaksi tidak valid.');
  };

  const handleSubmitRejectOrder = async () => {
    if (!rejectingOrder) return;
    setIsSubmittingReject(true);
    try {
      await handleUpdateStatus(
        rejectingOrder.id,
        'CANCELLED',
        'REJECTED',
        rejectionReasonInput.trim() || 'Pesanan ditolak oleh kasir outlet.'
      );
      showToast(`Pesanan #${rejectingOrder.orderNumber} telah DITOLAK.`, 'info');
      setRejectingOrder(null);
      setRejectionReasonInput('');
    } finally {
      setIsSubmittingReject(false);
    }
  };

  // Action: [ HAPUS PESANAN ]
  const handleOpenDeleteDialog = (order: CustomerOrder) => {
    if (isOutletAdmin && !matchesOutlet(order.outletId, assignedOutletId)) {
      showToast('Akses Ditolak: Anda hanya dapat memproses pesanan di cabang Anda.', 'error');
      return;
    }
    setOrderToDelete(order);
  };

  const handleConfirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    setIsSubmittingDelete(true);
    const targetId = orderToDelete.id;
    const targetNumber = orderToDelete.orderNumber;

    try {
      const result = await deleteOrder(targetId, assignedOutletId, auth.role);

      if (!result.success || !result.verified) {
        throw new Error(result.error || 'Gagal menghapus pesanan dari database Supabase.');
      }

      if (result.isArchived) {
        setOrders((prev) =>
          prev.map((o) =>
            o.id === targetId || o.orderNumber === targetNumber
              ? { ...o, orderStatus: 'CANCELLED', rejectionReason: 'Diarsip oleh Admin (Memiliki Riwayat Keuangan Courier Wallet)' }
              : o
          )
        );
        if (selectedOrderDetail?.id === targetId || selectedOrderDetail?.orderNumber === targetNumber) {
          setSelectedOrderDetail((prev) =>
            prev ? { ...prev, orderStatus: 'CANCELLED', rejectionReason: 'Diarsip oleh Admin (Memiliki Riwayat Keuangan Courier Wallet)' } : null
          );
        }
        showToast(result.message || `Pesanan #${targetNumber} memiliki riwayat keuangan Courier Wallet dan telah diubah statusnya menjadi DIBATALKAN / DIARSIP.`, 'info');
      } else {
        setOrders((prev) => prev.filter((o) => o.id !== targetId && o.orderNumber !== targetNumber));
        if (selectedOrderDetail?.id === targetId || selectedOrderDetail?.orderNumber === targetNumber) {
          setIsDetailModalOpen(false);
          setSelectedOrderDetail(null);
        }
        showToast(`Pesanan #${targetNumber} berhasil dihapus dari database.`, 'success');
      }
      setOrderToDelete(null);
    } catch (err: any) {
      console.error('[handleConfirmDeleteOrder Failed]:', err);
      const errMsg = err?.message || '';
      if (errMsg.includes('tidak ditemukan') || errMsg.includes('not found')) {
        setOrders((prev) => prev.filter((o) => o.id !== targetId && o.orderNumber !== targetNumber));
        if (selectedOrderDetail?.id === targetId || selectedOrderDetail?.orderNumber === targetNumber) {
          setIsDetailModalOpen(false);
          setSelectedOrderDetail(null);
        }
        showToast(`Pesanan #${targetNumber} tidak ditemukan di database (sudah dihapus). Tampilan diperbarui.`, 'info');
        setOrderToDelete(null);
      } else {
        showToast('Gagal menghapus pesanan: ' + (errMsg || 'Akses ditolak atau pesanan tidak ditemukan'), 'error');
      }
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  // ============================================================================
  // STRICT OUTLET + DATE SCOPING (WIB / Asia/Jakarta)
  // ============================================================================

  // 1. Base Outlet-Scoped Orders (Strict Outlet Isolation)
  const outletScopedOrders = useMemo(() => {
    return orders.filter((order) =>
      effectiveOutletScope === 'ALL' ? true : matchesOutlet(order.outletId, effectiveOutletScope)
    );
  }, [orders, effectiveOutletScope]);

  // 2. PESANAN HARI INI: Strictly orders created TODAY in Asia/Jakarta timezone
  const todayOrders = useMemo(() => {
    return outletScopedOrders.filter((order) => getJakartaDateKey(order.createdAt) === todayDateKey);
  }, [outletScopedOrders, todayDateKey]);

  // 3. RIWAYAT PESANAN: Strictly orders created BEFORE TODAY (< todayDateKey)
  const allPastOrders = useMemo(() => {
    return outletScopedOrders.filter((order) => {
      const k = getJakartaDateKey(order.createdAt);
      return Boolean(k && k < todayDateKey);
    });
  }, [outletScopedOrders, todayDateKey]);

  // Available historical dates that have orders (for quick-jump pills)
  const availableHistoryDates = useMemo(() => {
    const set = new Set<string>();
    set.add(yesterdayDateKey);
    allPastOrders.forEach((o) => {
      const k = getJakartaDateKey(o.createdAt);
      if (k && k < todayDateKey) {
        set.add(k);
      }
    });
    return Array.from(set).sort((a, b) => (a > b ? -1 : 1)).slice(0, 7);
  }, [allPastOrders, todayDateKey, yesterdayDateKey]);

  // 4. Orders for the SINGLE SELECTED HISTORY DATE
  const selectedDateHistoryOrders = useMemo(() => {
    if (!selectedHistoryDate || selectedHistoryDate >= todayDateKey) return [];
    return allPastOrders.filter((order) => getJakartaDateKey(order.createdAt) === selectedHistoryDate);
  }, [allPastOrders, selectedHistoryDate, todayDateKey]);

  // ============================================================================
  // SUMMARY STATISTICS
  // ============================================================================

  // Summary for PESANAN HARI INI (Computed strictly from todayOrders)
  const todayStats = useMemo(() => {
    // Hanya hitung pesanan yang siap diproses (kecualikan pesanan DANA yang belum PAID dari antrean utama)
    const verifiedTodayOrders = todayOrders.filter(
      (o) => !(o.paymentMethod === 'DANA' && o.paymentStatus !== 'PAID')
    );
    const total = verifiedTodayOrders.length;
    // Kecualikan pesanan DANA yang belum lunas dari newCount agar barista hanya melihat pesanan valid
    const newCount = verifiedTodayOrders.filter((o) => o.orderStatus === 'NEW').length;
    const acceptedCount = verifiedTodayOrders.filter((o) => o.orderStatus === 'ACCEPTED').length;
    const preparingCount = verifiedTodayOrders.filter((o) => o.orderStatus === 'PREPARING').length;
    const readyCount = verifiedTodayOrders.filter((o) => o.orderStatus === 'READY').length;
    const completedCount = verifiedTodayOrders.filter((o) => o.orderStatus === 'COMPLETED').length;
    const cancelledCount = todayOrders.filter((o) => o.orderStatus === 'CANCELLED').length;
    const waitingVerificationCount = todayOrders.filter(
      (o) => o.paymentStatus === 'WAITING VERIFICATION'
    ).length;
    const totalRevenue = verifiedTodayOrders
      .filter((o) => o.orderStatus !== 'CANCELLED')
      .reduce((acc, o) => acc + (o.totalAmount || 0), 0);

    return {
      total,
      newCount,
      acceptedCount,
      preparingCount,
      activeProcessCount: acceptedCount + preparingCount,
      readyCount,
      completedCount,
      cancelledCount,
      waitingVerificationCount,
      totalRevenue,
    };
  }, [todayOrders]);

  // Summary for RIWAYAT PESANAN (Computed strictly from selectedDateHistoryOrders)
  const historyDateStats = useMemo(() => {
    const total = selectedDateHistoryOrders.length;
    const completedCount = selectedDateHistoryOrders.filter(
      (o) => o.orderStatus === 'COMPLETED'
    ).length;
    const cancelledCount = selectedDateHistoryOrders.filter(
      (o) => o.orderStatus === 'CANCELLED'
    ).length;
    const activeRemainingCount = selectedDateHistoryOrders.filter(
      (o) => o.orderStatus !== 'COMPLETED' && o.orderStatus !== 'CANCELLED'
    ).length;
    const totalRevenue = selectedDateHistoryOrders
      .filter((o) => o.orderStatus !== 'CANCELLED')
      .reduce((acc, o) => acc + (o.totalAmount || 0), 0);

    return {
      total,
      completedCount,
      cancelledCount,
      activeRemainingCount,
      totalRevenue,
    };
  }, [selectedDateHistoryOrders]);

  // ============================================================================
  // SEARCH & STATUS FILTERING
  // ============================================================================

  const activeSourceOrders = viewMode === 'today' ? todayOrders : selectedDateHistoryOrders;

  const filteredOrders = useMemo(() => {
    return activeSourceOrders.filter((order) => {
      // Jangan tampilkan pesanan DANA yang belum lunas pada antrean aktif barista,
      // kecuali jika admin secara eksplisit memfilter status pembayaran/pembatalan terkait
      if (
        order.paymentMethod === 'DANA' &&
        order.paymentStatus !== 'PAID' &&
        selectedPaymentFilter !== 'WAITING PAYMENT' &&
        selectedPaymentFilter !== 'PAYMENT REJECTED' &&
        selectedStatusFilter !== 'CANCELLED'
      ) {
        return false;
      }
      const matchStatus =
        selectedStatusFilter === 'ALL' || order.orderStatus === selectedStatusFilter;
      const matchPayment =
        selectedPaymentFilter === 'ALL' || order.paymentStatus === selectedPaymentFilter;
      const q = searchQuery.trim().toLowerCase();
      const matchSearch =
        !q ||
        order.orderNumber.toLowerCase().includes(q) ||
        order.customerName.toLowerCase().includes(q) ||
        (order.customerPhone && order.customerPhone.toLowerCase().includes(q)) ||
        (order.tableNumber && order.tableNumber.toLowerCase().includes(q));

      return matchStatus && matchPayment && matchSearch;
    });
  }, [activeSourceOrders, selectedStatusFilter, selectedPaymentFilter, searchQuery]);

  const handleCopySql = () => {
    navigator.clipboard.writeText(ORDERS_SQL_SCHEMA);
    setCopiedSql(true);
    setTimeout(() => setCopiedSql(false), 2000);
  };

  // Helper to navigate history date by -1 or +1 day (strictly < todayDateKey)
  const handleShiftHistoryDate = (deltaDays: number) => {
    const nextDate = shiftJakartaDateKey(selectedHistoryDate, deltaDays);
    if (nextDate && nextDate < todayDateKey) {
      setSelectedHistoryDate(nextDate);
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* New Order Notification Banner */}
      <AnimatePresence>
        {notificationState.showBanner && notificationState.currentOrder && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="dark-navy-card fixed top-20 left-4 right-4 md:left-auto md:right-8 md:w-96 z-50 p-4 rounded-2xl bg-[#041d32] border border-[#00E5FF] shadow-2xl shadow-[#006389]/30"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#00E5FF]/20 flex items-center justify-center shrink-0 animate-pulse">
                <ShoppingBag className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-black text-white text-sm uppercase flex items-center gap-2">
                  <span className="text-[#00E5FF]">🔔</span> PESANAN BARU MASUK
                </h3>
                <div className="mt-2 space-y-1 text-xs text-slate-200 font-mono">
                  <p>
                    Order:{' '}
                    <span className="font-bold text-white">
                      {notificationState.currentOrder.orderNumber.startsWith('#')
                        ? notificationState.currentOrder.orderNumber
                        : `#${notificationState.currentOrder.orderNumber}`}
                    </span>
                  </p>
                  <p>
                    Customer:{' '}
                    <span className="text-white">{notificationState.currentOrder.customerName}</span>
                  </p>
                  <p className="text-amber-300 font-bold flex items-center gap-1">
                    <span>🕐 Pengambilan:</span>
                    <span className="font-mono bg-amber-950/80 px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-200">
                      {notificationState.currentOrder.pickupTime ||
                        notificationState.currentOrder.pickup_time ||
                        '-'}
                    </span>
                  </p>
                  <p>
                    Outlet:{' '}
                    <span className="text-white">{notificationState.currentOrder.outletName}</span>
                  </p>
                  <p>
                    Total:{' '}
                    <span className="font-bold text-[#00E5FF]">
                      {formatRupiah(notificationState.currentOrder.totalAmount)}
                    </span>
                  </p>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => {
                      handleSwitchViewMode('today');
                      setSelectedOrderDetail(notificationState.currentOrder);
                      setIsDetailModalOpen(true);
                      setNotificationState((prev) => ({ ...prev, showBanner: false }));
                    }}
                    className="flex-1 px-3 py-2 rounded-lg bg-[#00E5FF] text-slate-950 font-black text-[11px] uppercase tracking-wider hover:bg-white transition-colors cursor-pointer"
                  >
                    LIHAT PESANAN
                  </button>
                  <button
                    onClick={() =>
                      setNotificationState((prev) => ({ ...prev, showBanner: false }))
                    }
                    className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 font-bold text-[11px] uppercase tracking-wider hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================================================================== */}
      {/* STITCH TOP NAVIGATION & OUTLET HEADER BAR                            */}
      {/* ==================================================================== */}
      <div className="bg-white rounded-2xl border border-[#dbe9ff] p-4 sm:p-5 shadow-[0_2px_12px_rgba(0,99,137,0.04)] space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Left: Page Title & Active Date / Outlet Badge */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#eef4ff] border border-[#dbe9ff] text-[#006389] text-[11px] font-bold uppercase tracking-wider">
                <Building2 className="w-3.5 h-3.5" />
                <span>{isOutletAdmin ? assignedOutletName : 'Semua Cabang / Filter Aktif'}</span>
              </span>

              {viewMode === 'today' ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                  <span>LIVE HARI INI • {formatJakartaDateKeyId(todayDateKey, true)}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold">
                  <History className="w-3.5 h-3.5 text-amber-600" />
                  <span>ARSIP RIWAYAT • {formatJakartaDateKeyId(selectedHistoryDate, true)}</span>
                </span>
              )}
            </div>

            <h2 className="font-display font-black text-xl sm:text-2xl text-[#041d32] tracking-tight">
              {viewMode === 'today' ? 'Pesanan Hari Ini' : 'Riwayat Pesanan'}
            </h2>
            <p className="text-xs text-[#52606d] mt-0.5">
              {viewMode === 'today'
                ? 'Antrean operasional barista real-time khusus transaksi hari ini (WIB).'
                : `Menampilkan arsip transaksi khusus pada tanggal ${formatJakartaDateKeyId(selectedHistoryDate, false)}.`}
            </p>
          </div>

          {/* Right: Mode Switcher Tabs (Pesanan Hari Ini vs Riwayat Pesanan) + Utility Controls */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Primary View Switcher (Stitch Segmented Pill) */}
            <div className="inline-flex p-1 rounded-xl bg-[#eef4ff] border border-[#dbe9ff]">
              <button
                type="button"
                onClick={() => handleSwitchViewMode('today')}
                className={`px-3.5 py-2 rounded-lg text-xs font-extrabold transition-all flex items-center gap-1.5 cursor-pointer ${
                  viewMode === 'today'
                    ? 'bg-[#006389] text-white shadow-xs'
                    : 'text-[#3e484f] hover:text-[#041d32]'
                }`}
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                <span>Pesanan Hari Ini</span>
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                    viewMode === 'today'
                      ? 'bg-white/20 text-white'
                      : 'bg-[#dbe9ff] text-[#006389]'
                  }`}
                >
                  {todayStats.total}
                </span>
              </button>

              <button
                type="button"
                onClick={() => handleSwitchViewMode('history')}
                className={`px-3.5 py-2 rounded-lg text-xs font-extrabold transition-all flex items-center gap-1.5 cursor-pointer ${
                  viewMode === 'history'
                    ? 'bg-[#006389] text-white shadow-xs'
                    : 'text-[#3e484f] hover:text-[#041d32]'
                }`}
              >
                <History className="w-3.5 h-3.5" />
                <span>Riwayat Pesanan</span>
              </button>
            </div>

            {/* Audio Chime Toggle */}
            <button
              type="button"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2.5 rounded-xl border transition-colors flex items-center gap-1.5 text-xs font-bold cursor-pointer ${
                soundEnabled
                  ? 'bg-[#eef4ff] border-[#dbe9ff] text-[#006389]'
                  : 'bg-slate-100 border-slate-200 text-slate-500'
              }`}
              title={soundEnabled ? 'Notifikasi Suara Aktif' : 'Notifikasi Suara Nonaktif'}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <span className="hidden sm:inline">{soundEnabled ? 'Suara Aktif' : 'Mute'}</span>
            </button>

            {/* Super Admin Outlet Selector */}
            {!isOutletAdmin && (
              <select
                value={selectedOutletFilter}
                onChange={(e) => setSelectedOutletFilter(e.target.value)}
                className="px-3 py-2 rounded-xl bg-[#f8fbff] border border-[#dbe9ff] text-[#041d32] text-xs font-bold focus:outline-none focus:border-[#006389] cursor-pointer"
              >
                <option value="ALL">Semua Cabang</option>
                <option value="sudirman">Sudirman (Ch. 5)</option>
                <option value="kelakap_7">Kelakap 7 (Ch. 6)</option>
                <option value="letgo">Let'GO (MPP)</option>
              </select>
            )}

            {/* Super Admin SQL Helper */}
            {!isOutletAdmin && (
              <button
                type="button"
                onClick={() => setIsSqlModalOpen(true)}
                className="p-2.5 rounded-xl bg-[#f8fbff] hover:bg-[#eef4ff] border border-[#dbe9ff] text-[#006389] text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Skema SQL Supabase"
              >
                <Code className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Outlet Availability & Schedule Strip (Only on Pesanan Hari Ini) */}
        {viewMode === 'today' && (
          <div className="pt-3 border-t border-[#f1f5f9] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {isOutletAdmin ? (
              (() => {
                const normKey = normalizeOutletKey(assignedOutletId);
                const opStatus = resolveOutletOperatingStatus(normKey, outletsSchedules, outletsAvailability);
                const isOpen = opStatus.isOpen;
                const isUpdating = updatingOutletKey === normKey;
                return (
                  <>
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
                        }`}
                      />
                      <span className="text-xs font-bold text-[#041d32]">
                        Status Penerimaan Order ({assignedOutletName}):
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold ${
                          isOpen
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {isOpen ? 'BUKA (Menerima Order)' : 'TUTUP'}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        ({opStatus.mode === 'SCHEDULE' ? `Jadwal ${opStatus.openTime}-${opStatus.closeTime}` : opStatus.statusBadge})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenScheduleModal(normKey)}
                        className="px-3 py-1.5 rounded-xl font-bold text-xs bg-[#eef4ff] hover:bg-[#dbe9ff] text-[#006389] border border-[#dbe9ff] transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <Clock className="w-3.5 h-3.5" />
                        <span>Atur Jadwal</span>
                      </button>
                      <button
                        type="button"
                        disabled={isUpdating}
                        onClick={() => handleToggleOutletStatus(normKey, isOpen)}
                        className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                          isOpen
                            ? 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Power className="w-3.5 h-3.5" />
                        <span>
                          {isUpdating
                            ? 'Memproses...'
                            : isOpen
                            ? 'Tutup Manual'
                            : 'Buka Manual'}
                        </span>
                      </button>
                    </div>
                  </>
                );
              })()
            ) : (
              <div className="w-full flex flex-wrap items-center justify-between gap-2.5">
                <span className="text-xs font-bold text-[#52606d] flex items-center gap-1.5">
                  <Store className="w-3.5 h-3.5 text-[#006389]" />
                  <span>Jadwal & Status Operasional Outlet:</span>
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { id: 'sudirman', name: 'Sudirman' },
                    { id: 'kelakap_7', name: 'Kelakap 7' },
                    { id: 'letgo-mpp', name: "Let'GO" },
                  ].map((outlet) => {
                    const normId = normalizeOutletKey(outlet.id);
                    const opStatus = resolveOutletOperatingStatus(normId, outletsSchedules, outletsAvailability);
                    const isOpen = opStatus.isOpen;
                    const sched = outletsSchedules[normId] || DEFAULT_OUTLET_SCHEDULES[normId];
                    const mode = sched?.mode || 'SCHEDULE';

                    return (
                      <div
                        key={outlet.id}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border transition-all ${
                          isOpen
                            ? 'bg-emerald-50/90 border-emerald-200 text-emerald-900'
                            : 'bg-rose-50/90 border-rose-200 text-rose-900'
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
                          }`}
                        />
                        <span>{outlet.name}:</span>
                        <span className="font-extrabold">{isOpen ? 'Buka' : 'Tutup'}</span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {mode === 'SCHEDULE' ? `(${sched.openTime}-${sched.closeTime})` : `(${mode})`}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleOpenScheduleModal(outlet.id)}
                          className="ml-1 px-1.5 py-0.5 rounded bg-white/80 hover:bg-white text-[#006389] text-[10px] font-bold border border-slate-200 shadow-2xs hover:shadow-xs transition-all cursor-pointer"
                          title="Atur Jadwal & Mode"
                        >
                          Atur
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================================================================== */}
      {/* VIEW 1: PESANAN HARI INI (OPERATIONAL TODAY VIEW - STITCH DESIGN)    */}
      {/* ==================================================================== */}
      {viewMode === 'today' && (
        <>
          {/* Summary KPI Cards — Today Only */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-3.5">
            {/* 1. Pesanan Baru */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-[#52606d] tracking-wider">
                  Pesanan Baru
                </span>
                {todayStats.newCount > 0 && (
                  <span className="w-2 h-2 rounded-full bg-[#006389] animate-ping" />
                )}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-[#006389]">
                  {todayStats.newCount}
                </span>
                <span className="text-[11px] font-semibold text-[#006389]">Perlu Konfirmasi</span>
              </div>
            </div>

            {/* 2. Sedang Diproses */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <span className="text-[11px] font-bold uppercase text-[#52606d] tracking-wider block">
                Sedang Diproses
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-[#825100]">
                  {todayStats.activeProcessCount}
                </span>
                <span className="text-[11px] font-semibold text-[#825100]">
                  {todayStats.acceptedCount} Diterima • {todayStats.preparingCount} Diseduh
                </span>
              </div>
            </div>

            {/* 3. Siap Diambil */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <span className="text-[11px] font-bold uppercase text-[#52606d] tracking-wider block">
                Siap Diambil
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-emerald-700">
                  {todayStats.readyCount}
                </span>
                <span className="text-[11px] font-semibold text-emerald-700">Menunggu Pickup</span>
              </div>
            </div>

            {/* 4. Selesai Hari Ini */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <span className="text-[11px] font-bold uppercase text-[#52606d] tracking-wider block">
                Selesai Hari Ini
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-[#041d32]">
                  {todayStats.completedCount}
                </span>
                <span className="text-[11px] text-[#52606d] font-medium">
                  dari {todayStats.total} order
                </span>
              </div>
            </div>

            {/* 5. Omzet Hari Ini */}
            <div className="col-span-2 sm:col-span-1 p-4 rounded-2xl bg-[#041d32] text-[#FFFFFF] border border-[#006389]/40 shadow-[0_4px_12px_rgba(4,29,50,0.12)]">
              <span className="text-[11px] font-bold uppercase text-[#BAE6FD] tracking-wider block">
                Omzet Hari Ini
              </span>
              <div className="mt-2">
                <span className="font-mono font-black text-xl sm:text-2xl text-[#FFFFFF] drop-shadow-2xs">
                  {formatRupiah(todayStats.totalRevenue)}
                </span>
              </div>
              <span className="text-[11px] text-[#E0F2FE] font-semibold mt-0.5 block">
                Transaksi Aktif & Selesai
              </span>
            </div>
          </div>

          {/* Search & Filter Bar — Today */}
          <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.03)] space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-[#52606d] absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Cari #Nomor Order, Nama Pelanggan, Nomor Meja..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl bg-[#f8fbff] border border-[#c6d7e8] text-[#041d32] placeholder-[#6e7880] text-xs font-medium focus:outline-none focus:border-[#006389] transition-colors"
                />
              </div>

              {/* Payment Filter */}
              <div className="flex items-center gap-2">
                <select
                  value={selectedPaymentFilter}
                  onChange={(e) => setSelectedPaymentFilter(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-[#f8fbff] border border-[#c6d7e8] text-[#041d32] text-xs font-semibold focus:outline-none focus:border-[#006389] cursor-pointer"
                >
                  <option value="ALL">Semua Pembayaran</option>
                  <option value="WAITING VERIFICATION">Menunggu Verifikasi QRIS ({todayStats.waitingVerificationCount})</option>
                  <option value="PAID">Lunas (PAID)</option>
                  <option value="PAYMENT REJECTED">Ditolak (REJECTED)</option>
                  <option value="WAITING PAYMENT">Menunggu Pembayaran</option>
                </select>
              </div>
            </div>

            {/* Segmented Status Filter Pills (Stitch Design) */}
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pt-1" id="orderFilterTabs">
              {[
                { id: 'ALL', label: 'Semua', count: todayStats.total },
                { id: 'NEW', label: 'Baru', count: todayStats.newCount },
                { id: 'ACCEPTED', label: 'Diterima', count: todayStats.acceptedCount },
                { id: 'PREPARING', label: 'Diproses', count: todayStats.preparingCount },
                { id: 'READY', label: 'Siap Diambil', count: todayStats.readyCount },
                { id: 'COMPLETED', label: 'Selesai', count: todayStats.completedCount },
                { id: 'CANCELLED', label: 'Dibatalkan', count: todayStats.cancelledCount },
              ].map((tab) => {
                const isActive = selectedStatusFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedStatusFilter(tab.id)}
                    className={`flex items-center justify-center px-3.5 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      isActive
                        ? 'bg-[#006389] text-white shadow-xs'
                        : 'bg-[#f8fbff] text-[#3e484f] border border-[#dbe9ff] hover:bg-[#eef4ff]'
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                        isActive ? 'bg-white/20 text-white' : 'bg-[#eef4ff] text-[#006389]'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ==================================================================== */}
      {/* VIEW 2: RIWAYAT PESANAN (HISTORICAL SINGLE-DATE VIEW - STITCH)       */}
      {/* ==================================================================== */}
      {viewMode === 'history' && (
        <>
          {/* Date Selector & Quick Date Bar (Stitch Riwayat Pesanan Design) */}
          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-[#dbe9ff] shadow-[0_2px_10px_rgba(0,99,137,0.04)] space-y-3.5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#006389] flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>PILIH TANGGAL RIWAYAT PESANAN</span>
                </span>
                <h3 className="font-display font-black text-base sm:text-lg text-[#041d32] mt-0.5">
                  {formatJakartaDateKeyId(selectedHistoryDate, true)}
                </h3>
              </div>

              {/* Date Picker Controls */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleShiftHistoryDate(-1)}
                  className="p-2 rounded-xl bg-[#f8fbff] hover:bg-[#eef4ff] border border-[#c6d7e8] text-[#041d32] transition-colors cursor-pointer flex items-center gap-1 text-xs font-bold"
                  title="Tanggal Sebelumnya"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Sebelumnya</span>
                </button>

                <div className="relative">
                  <input
                    type="date"
                    value={selectedHistoryDate}
                    max={yesterdayDateKey}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && val < todayDateKey) {
                        setSelectedHistoryDate(val);
                      } else if (val >= todayDateKey) {
                        showToast('Riwayat hanya menampilkan pesanan sebelum hari ini.', 'info');
                        setSelectedHistoryDate(yesterdayDateKey);
                      }
                    }}
                    className="px-3.5 py-2 rounded-xl bg-[#eef4ff] border border-[#006389]/40 text-[#041d32] font-mono text-xs font-extrabold focus:outline-none focus:border-[#006389] cursor-pointer"
                  />
                </div>

                <button
                  type="button"
                  disabled={selectedHistoryDate >= yesterdayDateKey}
                  onClick={() => handleShiftHistoryDate(1)}
                  className={`p-2 rounded-xl border transition-colors flex items-center gap-1 text-xs font-bold ${
                    selectedHistoryDate >= yesterdayDateKey
                      ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-[#f8fbff] hover:bg-[#eef4ff] border-[#c6d7e8] text-[#041d32] cursor-pointer'
                  }`}
                  title="Tanggal Berikutnya"
                >
                  <span className="hidden sm:inline">Berikutnya</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Quick-Jump Recent Dates Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pt-2 border-t border-[#f1f5f9]">
              <span className="text-[11px] font-bold text-[#52606d] mr-1 shrink-0">
                Tanggal Cepat:
              </span>
              {availableHistoryDates.map((dKey) => {
                const isSelected = dKey === selectedHistoryDate;
                const isYesterday = dKey === yesterdayDateKey;
                const countOnDate = allPastOrders.filter(
                  (o) => getJakartaDateKey(o.createdAt) === dKey
                ).length;

                return (
                  <button
                    key={dKey}
                    type="button"
                    onClick={() => setSelectedHistoryDate(dKey)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-[#041d32] text-white shadow-xs'
                        : 'bg-[#f8fbff] text-[#3e484f] border border-[#dbe9ff] hover:bg-[#eef4ff]'
                    }`}
                  >
                    <span>
                      {isYesterday ? `Kemarin (${formatJakartaDateKeyId(dKey, false)})` : formatJakartaDateKeyId(dKey, false)}
                    </span>
                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                        isSelected ? 'bg-[#006389] text-white' : 'bg-[#eef4ff] text-[#006389]'
                      }`}
                    >
                      {countOnDate}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary KPI Cards — Strictly Scoped to SELECTED HISTORY DATE */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* 1. Total Pesanan pada Tanggal Terpilih */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <span className="text-[11px] font-bold uppercase text-[#52606d] tracking-wider block">
                Total Pesanan
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-[#041d32]">
                  {historyDateStats.total}
                </span>
                <span className="text-[11px] text-[#52606d] font-medium">Transaksi</span>
              </div>
              <span className="text-[10px] text-[#006389] font-semibold mt-1 block truncate">
                {formatJakartaDateKeyId(selectedHistoryDate, false)}
              </span>
            </div>

            {/* 2. Total Omzet pada Tanggal Terpilih */}
            <div className="p-4 rounded-2xl bg-[#041d32] text-[#FFFFFF] border border-[#006389]/40 shadow-[0_4px_12px_rgba(4,29,50,0.12)]">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-[#BAE6FD] tracking-wider">
                  Total Omzet
                </span>
                <TrendingUp className="w-4 h-4 text-[#38BDF8]" />
              </div>
              <div className="mt-2">
                <span className="font-mono font-black text-xl sm:text-2xl text-[#FFFFFF] drop-shadow-2xs">
                  {formatRupiah(historyDateStats.totalRevenue)}
                </span>
              </div>
              <span className="text-[11px] text-[#E0F2FE] font-semibold mt-1 block truncate">
                Non-Cancelled • {formatJakartaDateKeyId(selectedHistoryDate, false)}
              </span>
            </div>

            {/* 3. Pesanan Selesai pada Tanggal Terpilih */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <span className="text-[11px] font-bold uppercase text-emerald-700 tracking-wider block">
                Selesai (Completed)
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-emerald-700">
                  {historyDateStats.completedCount}
                </span>
                <span className="text-[11px] text-emerald-700 font-semibold">Berhasil</span>
              </div>
              <span className="text-[10px] text-[#52606d] mt-1 block">
                {historyDateStats.activeRemainingCount > 0
                  ? `+ ${historyDateStats.activeRemainingCount} status proses`
                  : 'Semua pesanan tuntas'}
              </span>
            </div>

            {/* 4. Pesanan Dibatalkan pada Tanggal Terpilih */}
            <div className="p-4 rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.04)]">
              <span className="text-[11px] font-bold uppercase text-[#ba1a1a] tracking-wider block">
                Dibatalkan (Cancelled)
              </span>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display font-black text-2xl sm:text-3xl text-[#ba1a1a]">
                  {historyDateStats.cancelledCount}
                </span>
                <span className="text-[11px] text-[#ba1a1a] font-semibold">Ditolak / Batal</span>
              </div>
              <span className="text-[10px] text-[#52606d] mt-1 block">
                Tidak dihitung ke omzet
              </span>
            </div>
          </div>

          {/* Search & Status Filter Bar — History Selected Date */}
          <div className="bg-white p-3.5 sm:p-4 rounded-2xl border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.03)] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-[#52606d] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={`Cari #Nomor Order atau Nama Pelanggan pada ${formatJakartaDateKeyId(selectedHistoryDate, false)}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-xl bg-[#f8fbff] border border-[#c6d7e8] text-[#041d32] placeholder-[#6e7880] text-xs font-medium focus:outline-none focus:border-[#006389] transition-colors"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              {[
                { id: 'ALL', label: 'Semua', count: historyDateStats.total },
                { id: 'COMPLETED', label: 'Selesai', count: historyDateStats.completedCount },
                { id: 'CANCELLED', label: 'Dibatalkan', count: historyDateStats.cancelledCount },
              ].map((tab) => {
                const isActive = selectedStatusFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedStatusFilter(tab.id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      isActive
                        ? 'bg-[#006389] text-white shadow-xs'
                        : 'bg-[#f8fbff] text-[#3e484f] border border-[#dbe9ff] hover:bg-[#eef4ff]'
                    }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ==================================================================== */}
      {/* ORDERS LIST RENDERING (TODAY CARDS OR HISTORY CARDS/TABLE)           */}
      {/* ==================================================================== */}
      {isLoading ? (
        <div className="py-20 text-center flex flex-col items-center justify-center bg-white rounded-2xl border border-[#dbe9ff]">
          <span className="w-8 h-8 border-2 border-[#006389]/30 border-t-[#006389] rounded-full animate-spin mb-3" />
          <p className="font-mono text-xs text-[#52606d]">Memuat data pesanan...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="py-16 px-4 text-center rounded-2xl bg-white border border-[#dbe9ff] shadow-[0_2px_8px_rgba(0,99,137,0.03)]">
          <ShoppingBag className="w-12 h-12 text-[#9fd4ff] mx-auto mb-3" />
          <p className="font-display font-bold text-[#041d32] text-base">
            {viewMode === 'today'
              ? 'Belum Ada Pesanan Hari Ini'
              : `Tidak Ada Pesanan pada ${formatJakartaDateKeyId(selectedHistoryDate, false)}`}
          </p>
          <p className="text-[#52606d] text-xs mt-1 max-w-md mx-auto">
            {viewMode === 'today'
              ? 'Pesanan baru yang dibuat oleh pelanggan hari ini akan otomatis tampil secara realtime di halaman ini.'
              : 'Silakan pilih tanggal lain pada kalender di atas atau ubah kata kunci pencarian Anda.'}
          </p>
        </div>
      ) : (
        <div
          id="orderCardsContainer"
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5 pb-8"
        >
          {filteredOrders.map((order) => {
            const isUpdating = updatingOrderId === order.id;
            const { timeStr, relStr } = getOrderTimestamp(order.createdAt);
            const isOrderToday = getJakartaDateKey(order.createdAt) === todayDateKey;

            return (
              <motion.div
                key={order.id}
                id={`order-${order.orderNumber}`}
                data-status={order.orderStatus.toLowerCase()}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => {
                  setSelectedOrderDetail(order);
                  setIsDetailModalOpen(true);
                }}
                className={`order-card group flex flex-col justify-between p-4 rounded-2xl bg-white shadow-[0_2px_10px_rgba(0,99,137,0.06)] border transition-all duration-200 cursor-pointer hover:shadow-md hover:border-[#006389]/40 ${
                  order.paymentStatus === 'WAITING VERIFICATION' && isOrderToday
                    ? 'border-amber-300 ring-1 ring-amber-400/30'
                    : order.orderStatus === 'NEW' && isOrderToday
                    ? 'border-[#006389]/40 ring-1 ring-[#006389]/20'
                    : 'border-[#dbe9ff]'
                }`}
              >
                <div>
                  {/* 1. Top Row: Order Number + Status Badge + Timestamp */}
                  <div className="flex items-center justify-between gap-2 pb-2.5 border-b border-[#f1f5f9]">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-extrabold text-[15px] sm:text-base text-[#041d32] tracking-tight truncate">
                        {order.orderNumber.startsWith('#')
                          ? order.orderNumber
                          : `#${order.orderNumber}`}
                      </span>

                      {/* Quick Kitchen Slip Print Icon */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrderForSlip(order);
                          setIsSlipModalOpen(true);
                        }}
                        title="Cetak Slip Dapur"
                        className="p-1 rounded-md text-[#006389] hover:bg-[#eef4ff] transition-colors cursor-pointer shrink-0"
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </button>

                      {/* Status Badge (Stitch Design) */}
                      {order.orderStatus === 'NEW' ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#c6e7ff] text-[#004c6b] shrink-0">
                          BARU
                        </span>
                      ) : order.orderStatus === 'ACCEPTED' ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#dbe9ff] text-[#004c6b] flex items-center gap-1 shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>DITERIMA</span>
                        </span>
                      ) : order.orderStatus === 'PREPARING' ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#ffddb8] text-[#653e00] flex items-center gap-1 shrink-0">
                          <Coffee className="w-3 h-3 animate-pulse" />
                          <span>DIPROSES</span>
                        </span>
                      ) : order.orderStatus === 'READY' ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#d1fae5] text-[#065f46] flex items-center gap-1 shrink-0">
                          <Bell className="w-3 h-3" />
                          <span>SIAP DIAMBIL</span>
                        </span>
                      ) : order.orderStatus === 'COMPLETED' ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#eef4ff] text-[#525e79] flex items-center gap-1 shrink-0">
                          <Check className="w-3 h-3 text-[#006389]" />
                          <span>SELESAI</span>
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#ffdad6] text-[#93000a] flex items-center gap-1 shrink-0">
                          <X className="w-3 h-3" />
                          <span>DIBATALKAN</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 text-[#3e484f] text-[11px] shrink-0 font-mono">
                      <Clock className="w-3 h-3 text-[#6e7880]" />
                      <span>
                        {isOrderToday ? (
                          <>
                            {timeStr}{' '}
                            {relStr && <span className="text-[#006389] font-medium">{relStr}</span>}
                          </>
                        ) : (
                          formatOrderTime(order.createdAt)
                        )}
                      </span>
                    </div>
                  </div>

                  {/* 2. Customer & Dining Context Bar */}
                  <div className="flex items-center justify-between py-1.5 px-2.5 rounded-xl bg-[#eef4ff] my-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <User className="w-3.5 h-3.5 text-[#006389] shrink-0" />
                      <span className="text-xs text-[#041d32] font-bold truncate">
                        {order.customerName}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[#007dad] text-xs font-bold shrink-0">
                      {order.orderType === 'DINE IN' ? (
                        <>
                          <Utensils className="w-3.5 h-3.5" />
                          <span>Dine In • Meja {order.tableNumber || '-'}</span>
                        </>
                      ) : order.orderType === 'DELIVERY' ? (
                        <>
                          <ShoppingBag className="w-3.5 h-3.5 text-[#006389]" />
                          <span className="text-[#006389]">Delivery</span>
                        </>
                      ) : (
                        <>
                          <ShoppingBag className="w-3.5 h-3.5 text-[#525e79]" />
                          <span className="text-[#525e79]">Take Away</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Read-only Courier Progress Badge for DELIVERY orders (independent of Admin Outlet workflow) */}
                  {order.orderType === 'DELIVERY' && (
                    <div className="flex items-center justify-between py-1 px-2.5 rounded-lg bg-sky-50 border border-sky-200/90 my-1.5 text-sky-950 text-[11px] font-bold">
                      <span className="text-sky-800">Status Kurir:</span>
                      <span className="px-2 py-0.5 rounded-md bg-white border border-sky-200 text-sky-900 font-extrabold">
                        {(() => {
                          const dStat = String(order.deliveryStatus || order.delivery_status || 'UNASSIGNED').toUpperCase().trim();
                          if (dStat === 'PICKED_UP') return 'Pesanan Dijemput';
                          if (dStat === 'ON_DELIVERY') return 'Sedang Diantar';
                          if (dStat === 'DELIVERED') return 'Selesai Diantar';
                          if (dStat === 'ASSIGNED') return 'Kurir Ditugaskan';
                          return 'Menunggu Dijemput';
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Pickup Time Badge */}
                  {(order.pickupTime || order.pickup_time) && (
                    <div className="flex items-center justify-between py-1 px-2.5 rounded-lg bg-amber-50 border border-amber-200/90 my-1.5 text-amber-950 font-black text-xs">
                      <span className="flex items-center gap-1.5 text-amber-900 font-extrabold">
                        <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>AMBIL:</span>
                      </span>
                      <span className="font-mono text-xs font-black text-amber-950 bg-white px-2 py-0.5 rounded-md border border-amber-300 shadow-2xs">
                        {order.pickupTime || order.pickup_time}
                      </span>
                    </div>
                  )}

                  {/* 3. Compact Menu Item Specs */}
                  <div className="flex flex-col gap-1.5 py-1.5">
                    {(order.items || []).slice(0, 3).map((it, idx) => {
                      const customSummary = getItemCustomizationSummary(it);
                      return (
                        <div key={idx} className="flex items-start gap-1.5 text-[#041d32] text-xs">
                          <span
                            className={`font-bold min-w-[22px] shrink-0 ${
                              order.orderStatus === 'PREPARING'
                                ? 'text-[#825100]'
                                : order.orderStatus === 'READY'
                                ? 'text-emerald-700'
                                : 'text-[#006389]'
                            }`}
                          >
                            {it.quantity}x
                          </span>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="font-semibold text-[#041d32] leading-snug truncate">
                              {it.name}
                            </span>
                            {customSummary ? (
                              <span
                                className="text-[11px] text-[#3e484f] truncate"
                                title={customSummary}
                              >
                                {customSummary}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                    {(order.items || []).length > 3 && (
                      <div className="text-[11px] text-[#006389] font-semibold pl-6 pt-0.5">
                        + {(order.items || []).length - 3} menu lainnya...
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  {/* 4. Total & Payment Verification Status */}
                  <div className="flex items-center justify-between pt-2.5 pb-2.5 mt-1 border-t border-[#f1f5f9]">
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-[#6e7880] font-bold">
                        Total Pembayaran
                      </span>
                      <span className="text-sm sm:text-[15px] font-extrabold text-[#041d32]">
                        {formatRupiah(order.totalAmount)}
                      </span>
                    </div>

                    {/* Payment Verification Status Badge */}
                    {order.paymentStatus === 'PAID' ? (
                      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#dbe9ff] text-[#006389] text-[11px] font-bold">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#006389]" />
                        <span>{order.paymentMethod || 'QRIS'} • Lunas</span>
                      </div>
                    ) : order.paymentStatus === 'PAYMENT REJECTED' ||
                      order.paymentStatus === 'REJECTED' ? (
                      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ffdad6] text-[#93000a] text-[11px] font-bold">
                        <X className="w-3.5 h-3.5" />
                        <span>{order.paymentMethod || 'QRIS'} • Ditolak</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ffddb8] text-[#2a1700] text-[11px] font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#825100] animate-pulse" />
                        <span>{order.paymentMethod || 'QRIS'} • Menunggu Cek</span>
                      </div>
                    )}
                  </div>

                  {/* 5. Action Buttons Footer */}
                  <div className="flex items-center justify-between gap-1.5 pt-2.5 border-t border-[#f1f5f9]">
                    {/* Left: Detail button & Delete */}
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrderDetail(order);
                          setIsDetailModalOpen(true);
                        }}
                        className="px-3 py-1.5 rounded-xl border border-[#c6d7e8] bg-white text-[#041d32] text-xs font-bold hover:bg-[#eef4ff] transition-colors cursor-pointer inline-flex items-center gap-1.5 shrink-0"
                      >
                        <Eye className="w-3.5 h-3.5 text-[#006389]" />
                        <span>Detail</span>
                      </button>

                      <button
                        type="button"
                        disabled={isUpdating}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(order);
                        }}
                        title="Hapus Pesanan"
                        className="p-1.5 rounded-xl border border-[#c6d7e8] bg-white text-[#52606d] hover:text-[#ba1a1a] hover:bg-[#ffdad6]/30 transition-colors cursor-pointer shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Right: Operational Barista Workflow Buttons (Active on Pesanan Hari Ini) */}
                    {isOrderToday ? (
                      <div className="flex items-center gap-1.5">
                        {/* Tolak Button */}
                        {order.orderStatus !== 'CANCELLED' && order.orderStatus !== 'COMPLETED' && (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRejectDialog(order);
                            }}
                            className="px-2.5 py-1.5 rounded-xl border border-[#ffdad6] bg-[#fff8f7] text-[#ba1a1a] text-xs font-bold hover:bg-[#ffdad6] transition-colors cursor-pointer shrink-0"
                          >
                            Tolak
                          </button>
                        )}

                        {/* Primary Status Progression Button */}
                        {order.orderStatus === 'NEW' ? (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptOrder(order);
                            }}
                            className="px-3.5 py-1.5 rounded-xl bg-[#006389] text-white text-xs font-bold hover:bg-[#004f6e] transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1 shrink-0"
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span>Terima Pesanan</span>
                          </button>
                        ) : order.orderStatus === 'ACCEPTED' ? (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartBrewing(order);
                            }}
                            className="px-3.5 py-1.5 rounded-xl bg-[#006389] text-white text-xs font-bold hover:bg-[#004f6e] transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1 shrink-0"
                          >
                            <Coffee className="w-3.5 h-3.5" />
                            <span>Proses Pesanan</span>
                          </button>
                        ) : order.orderStatus === 'PREPARING' ? (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkReady(order);
                            }}
                            className="px-3.5 py-1.5 rounded-xl bg-[#006389] text-white text-xs font-bold hover:bg-[#004f6e] transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1 shrink-0"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Tandai Siap Diambil</span>
                          </button>
                        ) : order.orderStatus === 'READY' ? (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompleteOrder(order);
                            }}
                            className="px-3.5 py-1.5 rounded-xl bg-[#041d32] text-white text-xs font-bold hover:bg-[#1a3349] transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1 shrink-0"
                          >
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Selesaikan Pesanan</span>
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      /* History Card Right Footer Info */
                      <span className="text-[11px] font-mono text-[#52606d]">
                        {formatJakartaDateKeyId(getJakartaDateKey(order.createdAt), false)}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Lightbox Preview Modal for Receipt */}
      {previewImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-black text-base text-white uppercase flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-[#00E5FF]" />
                  <span>Detail Bukti Pembayaran QRIS</span>
                </h3>
                <span className="text-xs text-slate-400 font-mono">
                  Periksa kejelasan nominal transfer, nama rekening tujuan, dan tanggal transaksi.
                </span>
              </div>
              <button
                onClick={() => setPreviewImageUrl(null)}
                className="p-2 text-slate-400 hover:text-white rounded-full bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto rounded-2xl bg-black/60 p-2 flex items-center justify-center">
              <img
                src={previewImageUrl}
                alt="Bukti Transfer Penuh"
                className="max-h-[65vh] w-auto object-contain rounded-xl shadow-lg"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <a
                href={previewImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-[#00E5FF] hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Buka gambar resolusi asli</span>
              </a>

              <button
                type="button"
                onClick={() => setPreviewImageUrl(null)}
                className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-display font-bold text-xs uppercase tracking-wider cursor-pointer"
              >
                TUTUP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal for TOLAK */}
      {rejectingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs animate-fadeIn">
          <div className="w-full max-w-md bg-white border border-[#dbe9ff] rounded-2xl p-6 shadow-2xl space-y-4 text-[#041d32]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#ba1a1a]">
                <XCircle className="w-5 h-5" />
                <h3 className="font-display font-black text-base uppercase">
                  TOLAK / BATALKAN PESANAN
                </h3>
              </div>
              <button
                onClick={() => setRejectingOrder(null)}
                className="p-1.5 text-[#52606d] hover:text-[#041d32] rounded-full bg-[#f8fbff] cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-[#3e484f]">
              Apakah Anda yakin ingin menolak pesanan{' '}
              <strong className="text-[#041d32] font-mono">#{rejectingOrder.orderNumber}</strong> (
              {rejectingOrder.customerName})?
            </p>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase text-[#52606d] block">
                Alasan Penolakan:
              </label>
              <textarea
                rows={3}
                value={rejectionReasonInput}
                onChange={(e) => setRejectionReasonInput(e.target.value)}
                placeholder="Contoh: Stok bahan habis, toko sedang tutup, bukti transfer tidak sesuai..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-[#f8fbff] border border-[#c6d7e8] text-[#041d32] text-xs focus:outline-none focus:border-[#ba1a1a] font-sans"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRejectingOrder(null)}
                className="px-4 py-2 rounded-xl bg-[#f8fbff] hover:bg-[#eef4ff] text-[#3e484f] font-bold text-xs uppercase tracking-wider border border-[#c6d7e8] cursor-pointer"
              >
                BATAL
              </button>
              <button
                type="button"
                disabled={isSubmittingReject}
                onClick={handleSubmitRejectOrder}
                className="px-5 py-2 rounded-xl bg-[#ba1a1a] hover:bg-[#93000a] text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                {isSubmittingReject ? <span>Menyimpan...</span> : <span>KONFIRMASI TOLAK</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deletion Confirmation Modal for HAPUS PESANAN */}
      {orderToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs animate-fadeIn">
          <div className="w-full max-w-md bg-white border border-[#dbe9ff] rounded-2xl p-6 shadow-2xl space-y-4 text-[#041d32]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#ba1a1a]">
                <Trash2 className="w-5 h-5" />
                <h3 className="font-display font-black text-base uppercase">HAPUS PESANAN</h3>
              </div>
              <button
                onClick={() => setOrderToDelete(null)}
                className="p-1.5 text-[#52606d] hover:text-[#041d32] rounded-full bg-[#f8fbff] cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 text-xs text-[#3e484f]">
              <p>
                Apakah Anda yakin ingin memproses penghapusan pesanan{' '}
                <strong className="text-[#041d32] font-mono">#{orderToDelete.orderNumber}</strong> (
                {orderToDelete.customerName})?
              </p>
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 font-medium">
                ℹ️ Sistem akan memverifikasi riwayat keuangan pesanan. Pesanan yang memiliki catatan Courier Wallet akan diarsipkan secara aman untuk menjaga integritas audit keuangan.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOrderToDelete(null)}
                className="px-4 py-2 rounded-xl bg-[#f8fbff] hover:bg-[#eef4ff] text-[#3e484f] font-bold text-xs uppercase tracking-wider border border-[#c6d7e8] cursor-pointer"
              >
                BATAL
              </button>
              <button
                type="button"
                disabled={isSubmittingDelete}
                onClick={handleConfirmDeleteOrder}
                className="px-5 py-2 rounded-xl bg-[#ba1a1a] hover:bg-[#93000a] text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                {isSubmittingDelete ? <span>Menghapus...</span> : <span>YA, HAPUS PESANAN</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SQL Schema Modal */}
      {isSqlModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display font-black text-lg text-white uppercase">
                  Skema Database Supabase untuk Online Ordering & Pembayaran
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Jalankan skrip ini sekali di SQL Editor Supabase untuk membuat tabel orders lengkap.
                </p>
              </div>
              <button
                onClick={() => setIsSqlModalOpen(false)}
                className="p-2 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-950 p-4 rounded-xl border border-slate-800 font-mono text-xs text-slate-300">
              <pre className="whitespace-pre-wrap">{ORDERS_SQL_SCHEMA}</pre>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-[11px] text-slate-400 font-mono">
                Copy script di atas lalu paste ke Supabase SQL Editor
              </span>
              <button
                onClick={handleCopySql}
                className="px-4 py-2 rounded-xl bg-[#00E5FF] text-slate-950 font-display font-bold text-xs flex items-center gap-2 cursor-pointer shadow-md shadow-cyan-500/20"
              >
                {copiedSql ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copiedSql ? 'Tersalin ke Clipboard!' : 'Salin Semua SQL'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kitchen / Bar Slip Modal */}
      <KitchenSlipModal
        isOpen={isSlipModalOpen}
        onClose={() => {
          setIsSlipModalOpen(false);
          setSelectedOrderForSlip(null);
        }}
        order={selectedOrderForSlip}
      />

      {/* Order Detail Modal (Stitch Design with Operational Actions for Today's Orders) */}
      <OrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedOrderDetail(null);
        }}
        order={orders.find((o) => o.id === selectedOrderDetail?.id) || selectedOrderDetail}
        isTodayOrder={
          selectedOrderDetail
            ? getJakartaDateKey(selectedOrderDetail.createdAt) === todayDateKey
            : false
        }
        isUpdating={Boolean(
          selectedOrderDetail && updatingOrderId === selectedOrderDetail.id
        )}
        onAcceptOrder={handleAcceptOrder}
        onStartBrewing={handleStartBrewing}
        onMarkReady={handleMarkReady}
        onCompleteOrder={handleCompleteOrder}
        onRejectOrder={(ord) => {
          setIsDetailModalOpen(false);
          handleOpenRejectDialog(ord);
        }}
        onPrintSlip={(ord) => {
          setSelectedOrderForSlip(ord);
          setIsSlipModalOpen(true);
        }}
      />

      {/* Outlet Operating Schedule & Mode Modal (Super Admin) */}
      {editingScheduleOutlet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-lg bg-white rounded-3xl p-6 shadow-2xl border border-[#dbe9ff] space-y-5 flex flex-col">
            <div className="flex items-center justify-between border-b border-[#f1f5f9] pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#006389] font-mono">
                  ADMIN PUSAT • PENGATURAN OPERASIONAL
                </span>
                <h3 className="font-display font-black text-lg text-[#041d32] uppercase">
                  Cabang: {editingScheduleOutlet.toUpperCase()}
                </h3>
              </div>
              <button
                onClick={() => setEditingScheduleOutlet(null)}
                className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Mode Selection */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-[#041d32] block">
                Status Mode Operasional:
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  {
                    value: 'SCHEDULE',
                    label: 'Ikuti Jadwal',
                    desc: 'Otomatis Buka & Tutup sesuai jam',
                  },
                  {
                    value: 'OPEN',
                    label: 'Buka Manual',
                    desc: 'Paksa Buka, abaikan jadwal',
                  },
                  {
                    value: 'CLOSED',
                    label: 'Tutup Manual',
                    desc: 'Paksa Tutup / Libur darurat',
                  },
                ].map((opt) => {
                  const isSelected = tempScheduleForm.mode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setTempScheduleForm((prev) => ({
                          ...prev,
                          mode: opt.value as OutletScheduleMode,
                        }))
                      }
                      className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                        isSelected
                          ? 'bg-[#eef4ff] border-[#006389] text-[#006389] ring-2 ring-[#006389]/20 shadow-xs'
                          : 'bg-white border-[#dbe9ff] text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between w-full mb-1">
                        <span className="font-extrabold text-xs">{opt.label}</span>
                        <span
                          className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                            isSelected
                              ? 'border-[#006389] bg-[#006389]'
                              : 'border-slate-300'
                          }`}
                        >
                          {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-500 leading-tight">
                        {opt.desc}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Schedule Hours (Only when Mode === 'SCHEDULE') */}
            {tempScheduleForm.mode === 'SCHEDULE' && (
              <div className="p-4 rounded-2xl bg-[#f8fbff] border border-[#dbe9ff] space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#006389]" />
                  <span className="text-xs font-bold text-[#041d32]">
                    Jam Operasional Otomatis (WIB / UTC+7)
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-[#52606d] block mb-1">
                      Jam Buka
                    </label>
                    <input
                      type="time"
                      value={tempScheduleForm.openTime || '08:00'}
                      onChange={(e) =>
                        setTempScheduleForm((prev) => ({
                          ...prev,
                          openTime: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 rounded-xl bg-white border border-[#c6d7e8] font-mono text-sm text-[#041d32] font-bold focus:outline-none focus:border-[#006389]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-[#52606d] block mb-1">
                      Jam Tutup
                    </label>
                    <input
                      type="time"
                      value={tempScheduleForm.closeTime || '23:00'}
                      onChange={(e) =>
                        setTempScheduleForm((prev) => ({
                          ...prev,
                          closeTime: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 rounded-xl bg-white border border-[#c6d7e8] font-mono text-sm text-[#041d32] font-bold focus:outline-none focus:border-[#006389]"
                    />
                  </div>
                </div>

                <p className="text-[10px] text-slate-500 leading-relaxed">
                  💡 Mendukung jam lewat tengah malam. Contoh: Buka <strong>08:00</strong> & Tutup <strong>00:00</strong> (Tutup saat tengah malam berganti hari) atau <strong>18:00</strong> & Tutup <strong>02:00</strong> (Dini hari).
                </p>
              </div>
            )}

            {/* Live Preview */}
            {(() => {
              const preview = resolveOutletOperatingStatus(
                editingScheduleOutlet,
                { [editingScheduleOutlet]: tempScheduleForm },
                null
              );
              return (
                <div
                  className={`p-3 rounded-xl border flex items-center justify-between text-xs ${
                    preview.isOpen
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-rose-50 border-rose-200 text-rose-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${
                        preview.isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
                      }`}
                    />
                    <span className="font-bold">Status Saat Ini:</span>
                    <span className="font-extrabold underline">
                      {preview.isOpen ? 'BUKA (Menerima Order)' : 'TUTUP (Order Ditutup)'}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-slate-600">
                    {preview.reason}
                  </span>
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#f1f5f9]">
              <button
                type="button"
                onClick={() => setEditingScheduleOutlet(null)}
                className="px-4 py-2 rounded-xl bg-[#f8fbff] hover:bg-[#eef4ff] text-[#3e484f] font-bold text-xs uppercase tracking-wider border border-[#c6d7e8] cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isSavingSchedule}
                onClick={handleSaveSchedule}
                className="px-5 py-2 rounded-xl bg-[#006389] hover:bg-[#004d6b] text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 cursor-pointer shadow-sm disabled:opacity-50"
              >
                {isSavingSchedule ? <span>Menyimpan...</span> : <span>Simpan Jadwal</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
